import { useState, useRef, useEffect } from "react";
import Autocomplete from "react-google-autocomplete";
import { getTravelPreferences } from "../../../actions/apiData";
import { useSelector } from "react-redux";
import solo from '../../../assets/images/solo.svg'
import couple from '../../../assets/images/couple.svg'
import Family from '../../../assets/images/Family.svg'
import Friends from '../../../assets/images/Friends.svg'
import hiking from '../../../assets/images/hiking.svg'
import Relaxation from '../../../assets/images/Relaxation.svg'
import Nightlife from '../../../assets/images/Nightlife.svg'
import Foodie from '../../../assets/images/Foodie.svg'
import Shopping from '../../../assets/images/Shopping.svg'
import Camping from '../../../assets/images/Camping.svg'
import Culture from '../../../assets/images/Culture.svg'
import Festivals from '../../../assets/images/Festivals.svg'
import planetakeoff from '../../../assets/images/plane-takeoff.svg'
import calendar from '../../../assets/images/calendar.svg'
import Destination from '../../../assets/images/Destination.svg'

export function capitalizeWords(str = "") {
    return str
        .trim()
        .split(" ")
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

export default function TravelPreferencesForm({ onSubmit, disabled }) {
    const headerplace = useSelector(state => state.place);
    const [formData, setFormData] = useState({
        destination: "",
        departure: "",
        travelDate: "",
        days: "",
        budget: "",
        travelWith: "",
        members: "",
        activities: []
    });
    const [activities, setActivities] = useState([]);
    const [errors, setErrors] = useState({});

    const [loading, setLoading] = useState(false);
    const [loadingActivities, setLoadingActivities] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    // Refs for fields to autofocus
    const destinationRef = useRef(null);
    const departureRef = useRef(null);
    const travelDateRef = useRef(null);
    const daysRef = useRef(null);
    const travelWithRef = useRef(null);
    const debounceRef = useRef(null);

    const travelWithOptions = [
        { label: "Solo", icon: solo, className: "solo-box" },
        { label: "Couple", icon: couple, className: "couple-box" },
        { label: "Family", icon: Family, className: "family-box" },
        { label: "Friends", icon: Friends, className: "friends-box" },
    ]
    const activitiesOptions = [
        { label: "Hiking", icon: hiking },
        { label: "Relaxation", icon: Relaxation },
        { label: "Nightlife", icon: Nightlife },
        { label: "Foodie", icon: Foodie },
        { label: "Shopping", icon: Shopping },
        { label: "Camping", icon: Camping },
        { label: "Culture", icon: Culture },
        { label: "Festivals", icon: Festivals },
    ]

    useEffect(() => {
        if (headerplace) {
            setFormData(prev => ({
                ...prev,
                departure: headerplace
            }));
        }
    }, [headerplace]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "destination" || name === "departure") return;
        setFormData({ ...formData, [name]: value });
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.destination.trim()) {
            newErrors.destination = "Destination is required!";
        }
        if (!formData.days.trim()) {
            newErrors.days = "Number of days to travel is required!";
        }
        if (!formData.departure.trim()) {
            newErrors.departure = "Departure city is required!";
        }
        if (!formData.travelDate) {
            newErrors.travelDate = "Travel date is required!";
        } else {
            const today = new Date().setHours(0, 0, 0, 0);
            const selectedDate = new Date(formData.travelDate).setHours(0, 0, 0, 0);
            if (selectedDate < today) {
                newErrors.travelDate = "Travel date cannot be earlier than today!";
            }
        }
        // Travel With validation
        if (!formData.travelWith.trim()) {
            newErrors.travelWith = "Please select who you're traveling with!";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length ? newErrors : null;
    };

    const fetchPreferences = (city = "") => {

        setActivities([]);      // clear old
        setLoadingActivities(true);       // show spinner (shared with form)

        getTravelPreferences({ destination: city })
            .then((data) => {
                if (data?.preferences?.length) {
                    setActivities(data.preferences);
                    setFormData((prev) => ({ ...prev, activities: [] })); // reset selections
                } else {
                    setActivities([]);
                }
            })
            .catch((err) => {
                console.error(err);
                setActivities([]);
            })
            .finally(() => {
                setLoadingActivities(false); // stop spinner
            });
    };



    const handleActivityClick = (activity) => {
        setFormData((prev) => {
            const alreadySelected = prev.activities.includes(activity);
            return {
                ...prev,
                activities: alreadySelected
                    ? prev.activities.filter((a) => a !== activity)
                    : [...prev.activities, activity]
            };
        });
    };


    const handleSubmit = (e) => {
        e.preventDefault();
        const error = validateForm();

        if (error) {
            const firstField = Object.keys(error)[0];
            setToastMessage(error[firstField]);
            setShowToast(true);

            // Autofocus
            switch (firstField) {
                case "destination":
                    destinationRef.current?.focus();
                    break;
                case "departure":
                    departureRef.current?.focus();
                    break;
                case "travelDate":
                    travelDateRef.current?.focus();
                    break;
                case "travelWith":
                    travelWithRef.current?.focus();
                    break;
                default:
                    break;
            }
            return;
        }

        setShowToast(false);
        setLoading(true);

        setTimeout(() => {
            setLoading(false);
            onSubmit(formData);
        }, 1000);
    }

    const handleSearch = (e) => {
        if (e.key === "Enter") e.preventDefault();
    };

    useEffect(() => {
        if (headerplace) {
            setFormData(prev => ({
                ...prev,
                departure: capitalizeWords(headerplace)
            }));
        }
    }, [headerplace]);


    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 2000); // 2 seconds
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    useEffect(() => {
        fetchPreferences();
    }, []);

    const today = new Date().toISOString().split("T")[0];

    // Date 2 years from today
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);
    const maxAllowedDate = maxDate.toISOString().split("T")[0];

    return (
        <section className="planTravelmodal section-equal-padding bg-white preferences_page">
            <div className="container-fluid">
                <h4>Tell us your travel preferences</h4>
                <p>Just provide some basic information, and our trip planner will generate
                    a customized itinerary based on your preferences.</p>

                {showToast && (
                    <div
                        className="toast align-items-center text-bg-danger border-0 show position-fixed fade-in"
                        style={{ top: 20, right: 20, zIndex: 9999 }}
                        role="alert"
                    >
                        <div className="toast-body">{toastMessage}</div>
                    </div>
                )}

                <div className="modal-contentTravel">
                    <div className="destination-block-modal">
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="modal-form-label">Destination</label>
                                <div className="row gx-3">
                                    <div className="col-12 col-sm-6 col-md-6 col-lg-6 mb-4">
                                        <div className="destination-form-input">
                                            <Autocomplete
                                                apiKey="AIzaSyDrSVPFOzjxmlv2WLSnfJ0ARrmYP9QDajs"
                                                name="destination"
                                                ref={destinationRef}
                                                onPlaceSelected={(place) => {
                                                    const addressComponents = place.address_components || [];
                                                    let city = "";

                                                    for (let comp of addressComponents) {
                                                        if (comp.types.includes("locality")) {
                                                            city = comp.long_name;
                                                            break;
                                                        }
                                                    }

                                                    if (!city && place.formatted_address) {
                                                        city = place.formatted_address.split(",")[0];
                                                    }

                                                    // ✅ reset destination & activities, then fetch
                                                    setFormData((prev) => ({ ...prev, destination: city, activities: [] }));


                                                }}
                                                className="form-control"
                                                placeholder="Where would you like to go"
                                                value={formData.destination}
                                                onChange={(e) => {
                                                    const value = e.target.value || "";
                                                    setFormData((prev) => ({ ...prev, destination: value.split(",")[0] }));
                                                }}
                                                onKeyPress={handleSearch}

                                            />
                                            <span className="destination-form-icon"><img
                                                src={planetakeoff} /></span>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm-6 col-md-6 col-lg-6 mb-4">
                                        <div className="destination-form-input">
                                            <Autocomplete
                                                apiKey="AIzaSyDrSVPFOzjxmlv2WLSnfJ0ARrmYP9QDajs"
                                                name="destination"
                                                ref={departureRef}
                                                onPlaceSelected={(place) => {
                                                    const addressComponents = place.address_components || [];
                                                    let city = "";

                                                    for (let comp of addressComponents) {
                                                        if (comp.types.includes("locality")) {
                                                            city = comp.long_name;
                                                            break;
                                                        }
                                                    }

                                                    if (!city && place.formatted_address) {
                                                        city = place.formatted_address.split(",")[0];
                                                    }

                                                    setFormData((prev) => ({ ...prev, departure: city }));
                                                }}
                                                onChange={(e) => {
                                                    const value = e.target.value || "";
                                                    setFormData((prev) => ({ ...prev, departure: value.split(",")[0] }));
                                                }}
                                                className="form-control"
                                                placeholder="Where would you like to go"
                                                value={formData.departure}
                                                onKeyPress={handleSearch}

                                            />
                                            <span className="destination-form-icon"><img
                                                src={Destination} alt="Destination" /></span>
                                        </div>

                                    </div>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="modal-form-label">Select the date when you go.</label>
                                <div className="row gx-3">
                                    <div className="col-12 col-sm-6 col-md-6 col-lg-4 mb-4 mb-lg-0">
                                        <div className="destination-form-input">
                                            <input
                                                type="date"
                                                name="travelDate"
                                                ref={travelDateRef}
                                                value={formData.travelDate}
                                                min={today}              // 👈 cannot choose past dates
                                                max={maxAllowedDate}     // 👈 cannot choose beyond 2 years
                                                onClick={(e) => {
                                                    if (e.target.showPicker) {
                                                        e.target.showPicker();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setFormData({ ...formData, travelDate: value });

                                                    // Validation (optional)
                                                    const selected = new Date(value).setHours(0, 0, 0, 0);
                                                    const minDateValue = new Date(today).setHours(0, 0, 0, 0);
                                                    const maxDateValue = new Date(maxAllowedDate).setHours(0, 0, 0, 0);

                                                    if (selected < minDateValue) {
                                                        setErrors(prev => ({
                                                            ...prev,
                                                            travelDate: "Travel date cannot be earlier than today!"
                                                        }));
                                                    } else if (selected > maxDateValue) {
                                                        setErrors(prev => ({
                                                            ...prev,
                                                            travelDate: "Travel date cannot be beyond 2 years from now!"
                                                        }));
                                                    } else {
                                                        setErrors(prev => {
                                                            const { travelDate, ...rest } = prev;
                                                            return rest;
                                                        });
                                                    }
                                                }}
                                                className="form-control"
                                            />
                                            <span className="destination-form-icon"><img
                                                src={calendar} alt="Calendar" /></span>
                                        </div>
                                        {errors.travelDate && (
                                            <p className="error-text">{errors.travelDate}</p>
                                        )}
                                    </div>
                                    <div className="col-12 col-sm-6 col-md-6 col-lg-4 mb-4 mb-lg-0">
                                        <div className="destination-form-input">
                                            <input
                                                type="text"
                                                name="days"
                                                ref={daysRef}
                                                value={formData.days}
                                                onChange={(e) => {
                                                    // Allow only digits and max 14
                                                    const cleaned = e.target.value.replace(/\D/g, "");
                                                    const limited = cleaned.slice(0, 2); // max 2 digits
                                                    let value = limited;
                                                    let error = "";
                                                    if (value === "") {
                                                        error = "Number of days to travel is required!";
                                                    } else if (parseInt(value, 10) > 31) {
                                                        value = "31";
                                                        error = "Maximum allowed days is 31";
                                                    }
                                                    setFormData({ ...formData, days: value });
                                                    setErrors(prev => {
                                                        const { days, ...rest } = prev;
                                                        return error ? { ...rest, days: error } : rest;
                                                    });
                                                }}
                                                placeholder="Enter Number Of Days"
                                                className="form-control"
                                                maxLength={2}
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                            />
                                            <span className="destination-form-icon"><img
                                                src={calendar} alt="Calendar" /></span>
                                        </div>
                                        {errors.days && (
                                            <p className="error-text">{errors.days}</p>
                                        )}
                                    </div>
                                    <div className="col-12 col-sm-6 col-md-6 col-lg-4 mb-4 mb-lg-0">
                                        <div className="destination-form-input">
                                            <input
                                                type="text"
                                                name="budget"
                                                value={formData.budget}
                                                onChange={(e) => {
                                                    // Allow only digits
                                                    const cleaned = e.target.value.replace(/\D/g, "");
                                                    setFormData({ ...formData, budget: cleaned });
                                                }}
                                                onKeyDown={(e) => {
                                                    // Block e, E, +, -, ., and space
                                                    if (["e", "E", "+", "-", ".", " "].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                placeholder="Budget (USD)"
                                                className="form-control"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="destination-block-modal">
                                <label className="modal-form-label">Who do you want to go with?</label>
                                <div className="d-flex flex-wrap gap-3 mt-2 travel-with-box">
                                    {travelWithOptions.map((type) => (
                                        <div
                                            key={type.label}
                                            onClick={() =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    travelWith: type.label,
                                                }))
                                            }
                                        >
                                            <div className={` ${formData.travelWith === type.label ? 'active' : ''} ${type.className} common-with-box`}>
                                                <span>
                                                    <img src={type.icon} />
                                                </span>
                                                <p>{type.label}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                            </div>
                            <div className="destination-block-modal">
                                <label className="modal-form-label">Activities</label>
                                <div className="d-flex flex-wrap justify-content-md-between" style={{ gap: '20px' }}>
                                    {activitiesOptions.map((activity) => (
                                        <div className="activities-block" key={activity.label}>
                                            <div className="activities-block">
                                                <input type="checkbox" id={activity.label} checked={formData.activities.includes(activity.label)} onChange={() => handleActivityClick(activity.label)} />
                                                <label htmlFor={activity.label}>
                                                    <img src={activity.icon} />
                                                    <p>{activity.label}</p>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="modal-action-btn mt-3">
                                <button className="form-button fill-button" type="submit" disabled={loading || disabled}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Submitting...
                                        </>
                                    ) : (
                                        "Submit"
                                    )}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
